import React, { useContext, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Grid, Typography } from "@mui/material";
import { ClientsContext } from "../context/ClientsContextProvider";
import { PostClient } from "../types";
import TextInput from "../../../main/components/TextInput";
import {
  clientCollaboratorsDataToClientCollaborators,
  clientToClientCollaboratorsData,
} from "../clientCollaboratorsMapper";
import SelectUsinasTable from "../../../main/components/SelectUsinasTable";
import SelectInput, { Option } from "../../../main/components/SelectInput";
import CardBox from "../../../main/components/CardBox";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import { emailRegex, phoneRegex } from "../../../main/utils/utils";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import { usersApi } from "../../../services/api";
import { Role } from "../../users/types";

const collaboratorSchema = yup.object().shape({
  name: yup.string().nullable(),
  email: yup
    .string()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .matches(emailRegex, "Formato de email inválido"),
  phone: yup
    .string()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .matches(phoneRegex, "Formato de Telefone inválido"),
  usinasIds: yup.array().of(yup.string().required()).nullable(),
  role: yup.string().nullable(),
});

const schema = yup.object().shape({
  collaborators: yup.array().of(collaboratorSchema),
});

export type ClientCollaboratorsData = yup.InferType<typeof schema>;
export type ClientCollaboratorData = yup.InferType<typeof collaboratorSchema>;

const ClientCollaboratorsForm = React.forwardRef(
  (
    props: {
      isClientCollaboratorEdit?: boolean;
      selectedStep?: number;
      setSelectedStep?: React.Dispatch<React.SetStateAction<number>>;
    },
    ref
  ) => {
    const { client, setClient, clientUsinasIds } = useContext(ClientsContext);

    const [allRoles, setAllRoles] = React.useState<Option[]>([]);

    const {
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      watch,
    } = useForm({
      resolver: yupResolver(schema),
    });
    const {
      fields: collaborators,
      append: appendCollaborator,
      remove: removeCollaborator,
    } = useFieldArray({
      control,
      name: "collaborators",
    });
    function updateData() {
      const data = clientToClientCollaboratorsData(client);
      if (!data) return;
      if (
        !(
          data.collaborators?.length ||
          (data.collaborators?.length && data.collaborators?.length > 0)
        )
      ) {
        setValue("collaborators", [{}]);
        return;
      }
      setValue("collaborators", data.collaborators);
    }
    useEffect(() => {
      updateData();
    }, [client]);

    const onSubmit = async (data: ClientCollaboratorsData) => {
      if (
        !(
          data.collaborators?.length ||
          (data.collaborators?.length && data.collaborators?.length > 0)
        )
      ) {
        setValue("collaborators", undefined);
      }
      const collaborators = clientCollaboratorsDataToClientCollaborators(data);
      const collaboratorsLength = collaborators?.length ?? 0;
      const newClientData: PostClient = {
        ...client,
        collaboratorsList: collaboratorsLength > 0 ? collaborators : undefined,
      };
      setClient(newClientData);
      props.selectedStep &&
        props.setSelectedStep &&
        props.setSelectedStep(props.selectedStep + 1);
    };
    const onMount = async () => {
      const roles = await usersApi.getAllRoles();
      const parsedRoles = roles.map((role: Role) => ({
        value: role.name,
        label: role.name,
      }));

      setAllRoles(parsedRoles);
    };

    useEffect(() => {
      onMount();
    }, []);

    React.useImperativeHandle(ref, () => ({
      async submitForm() {
        await handleSubmit(onSubmit)();
      },
    }));

    return (
      <Grid
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          paddingX: "10px",
          // justifyContent: "end",
          // alignItems: "center",
        }}
      >
        <Grid item xs={12} textAlign={"start"}>
          <Typography
            variant="h6"
            sx={{
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
              marginBottom: "15px",
              marginTop: "30px",
            }}
          >
            Colaborador
          </Typography>
        </Grid>
        <Grid
          component={"form"}
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid
            sx={{
              width: "100%",
              display: { xs: "none", md: "flex" },
              justifyContent: "end",
            }}
          >
            <PrimaryBaseButton
              onClick={() => {
                appendCollaborator({});
              }}
              title="adicionar colaborador"
              startIcon={<AddIcon />}
              sx={{
                width: "20%",
                marginBottom: "20px",
                height: "38px",
              }}
            />
          </Grid>

          {collaborators.map((field, index) => {
            const selectedUsinasIds = watch(`collaborators.${index}.usinasIds`);
            return (
              <CardBox
                sx={{
                  paddingTop: "25px",
                  paddingBottom: "25px",
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "20px",
                  width: "100%",
                }}
                key={field.id}
              >
                <Grid
                  spacing={2}
                  container
                  component={"form"}
                  width={"100%"}
                  paddingX={"10px"}
                >
                  <Grid display={"flex"} item xs={12} md={6}>
                    <TextInput
                      control={control}
                      fieldName={`collaborators[${index}].name`}
                      label="Nome"
                      isFromArray
                      errors={
                        errors.collaborators
                          ? errors.collaborators[index]
                          : undefined
                      }
                      sx={{
                        width: "100%",
                        // maxWidth: "280px",
                      }}
                    />
                  </Grid>
                  <Grid display={"flex"} item xs={12} md={6}>
                    <PhoneNumberInput
                      control={control}
                      fieldName={`collaborators[${index}].phone`}
                      label="Telefone"
                      isFromArray
                      errors={
                        errors.collaborators
                          ? errors.collaborators[index]
                          : undefined
                      }
                      sx={{
                        width: "100%",
                        // maxWidth: "280px",
                      }}
                    />
                  </Grid>
                  <Grid display={"flex"} item xs={12} md={6}>
                    <TextInput
                      control={control}
                      fieldName={`collaborators[${index}].email`}
                      label="Email"
                      errors={
                        errors.collaborators
                          ? errors.collaborators[index]
                          : undefined
                      }
                      isFromArray
                      sx={{
                        width: "100%",
                        // maxWidth: "280px",
                      }}
                    />
                  </Grid>
                  <Grid display={"flex"} item xs={12} md={6}>
                    <SelectInput
                      control={control}
                      isFromArray
                      fieldName={`collaborators[${index}].role`}
                      label="Perfil do Colaborador"
                      errors={
                        errors.collaborators
                          ? errors.collaborators[index]
                          : undefined
                      }
                      sx={{
                        width: "100%",
                        // maxWidth: "280px",
                      }}
                      options={allRoles}
                    />
                  </Grid>
                </Grid>

                <Grid
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                    textAlign: "start",
                  }}
                >
                  {clientUsinasIds.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: 16,
                          marginBottom: "16px",
                        }}
                      >
                        Selecione as Usinas do colaborador:
                      </Typography>
                      <CardBox sx={{ width: "100%" }}>
                        <SelectUsinasTable
                          avaliableUsinasIds={clientUsinasIds}
                          setValue={setValue}
                          selectedUsinasIds={selectedUsinasIds || []}
                          fieldName={`collaborators.${index}.usinasIds`}
                        />
                      </CardBox>
                    </>
                  )}
                  {index > 0 && (
                    <Box
                      sx={{
                        marginTop: "10px",
                      }}
                    >
                      <DangerBaseButton
                        onClick={() => {
                          removeCollaborator(index);
                        }}
                        startIcon={<DeleteIcon />}
                        title="Remover "
                        // title="Remover colaborador"
                      />
                    </Box>
                  )}
                </Grid>
              </CardBox>
            );
          })}
        </Grid>
      </Grid>
    );
  }
);

export default ClientCollaboratorsForm;
